import * as z from "zod/v4";

export const MERCHANT_SETUP_PROGRESS_TABLE = "merchant_setup_progress";

/**
 * Mirrors the merchant_setup_product CRDB ENUM. Adding a value here also
 * requires an ALTER TYPE migration.
 */
export const MerchantSetupProductSchema = z.enum(["disputes", "shopperAgent"]);
export type MerchantSetupProduct = z.infer<typeof MerchantSetupProductSchema>;

/**
 * Per-task interaction state. All fields optional — derived completion is
 * computed elsewhere from team settings via predicates. This blob only
 * records what the user has done with the checklist row.
 */
export const TaskStateSchema = z.object({
  visitedAt: z.iso.datetime().optional(),
  dismissedAt: z.iso.datetime().optional(),
  manuallyCompletedAt: z.iso.datetime().optional(),
  manuallyCompletedBy: z.string().optional(),
});
export type TaskState = z.infer<typeof TaskStateSchema>;

/**
 * Map of task ID → task state. JSONB shape in DB. Keys are TS-side task
 * identifiers; renames are handled by task-id-migrations.ts at read time.
 */
export const TaskStatesSchema = z.record(z.string(), TaskStateSchema);
export type TaskStates = z.infer<typeof TaskStatesSchema>;

/**
 * Intake answer values. Discriminator on question type lives in the TS
 * registry; storage is intentionally permissive so adding question types
 * doesn't require a schema migration.
 */
export const IntakeAnswerValueSchema = z.union([
  z.string(),
  z.number(),
  z.boolean(),
  z.array(z.string()),
]);
export type IntakeAnswerValue = z.infer<typeof IntakeAnswerValueSchema>;

export const IntakeAnswersSchema = z.record(
  z.string(),
  IntakeAnswerValueSchema,
);
export type IntakeAnswers = z.infer<typeof IntakeAnswersSchema>;

/**
 * Full row shape. Mirrors the columns + types in the
 * merchant_setup_progress table definition.
 */
export const MerchantSetupProgressSchema = z.object({
  id: z.bigint(),
  teamId: z.string(),
  product: MerchantSetupProductSchema,
  intakeAnswers: IntakeAnswersSchema,
  intakeCompletedAt: z.coerce.date().nullable(),
  intakeCompletedByUserId: z.string().nullable(),
  taskStates: TaskStatesSchema,
  entrySource: z.string().nullable(),
  startedAt: z.coerce.date(),
  completedAt: z.coerce.date().nullable(),
  completedByUserId: z.string().nullable(),
  dismissedAt: z.coerce.date().nullable(),
  dismissedByUserId: z.string().nullable(),
  /**
   * When set + in the future, the intake modal is suppressed until this
   * timestamp passes. Distinct from `dismissedAt` (permanent team-level
   * dismissal): a deferral expires automatically and the modal returns
   * once the merchant lands back inside the product's routes.
   */
  deferredUntil: z.coerce.date().nullable(),
  lastActiveAt: z.coerce.date().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});
export type MerchantSetupProgress = z.infer<typeof MerchantSetupProgressSchema>;
